import { useCallback, useEffect, useState } from 'react';
import { fetchMerchantEarnings, type EarningsSummary } from '@/lib/earnings';

// ─────────────────────────────────────────────────────────────
// Onglet « Mes gains » (côté commerçant).
// Ce que le commerce a réellement encaissé : ventes boutique et
// réservations payées en ligne, frais plateforme déduits, net à retirer.
// ─────────────────────────────────────────────────────────────

interface Props {
  userId: number;
  currency: string;
  onRequestWithdrawal?: () => void;
}

function formatDate(value: string): string {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' });
}

export default function EarningsTab({ userId, currency, onRequestWithdrawal }: Props) {
  const [summary, setSummary] = useState<EarningsSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const result = await fetchMerchantEarnings(userId, currency);
      setSummary(result);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Impossible de charger vos gains.');
    } finally {
      setLoading(false);
    }
  }, [userId, currency]);

  useEffect(() => { void load(); }, [load]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <i className="ri-loader-4-line animate-spin text-2xl text-primary-500"></i>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-background-50 border border-background-200/70 rounded-lg py-14 text-center">
        <div className="w-14 h-14 rounded-full bg-red-50 flex items-center justify-center mx-auto mb-3">
          <i className="ri-error-warning-line text-2xl text-red-500"></i>
        </div>
        <p className="text-sm text-foreground-600 mb-4">{error}</p>
        <button
          type="button"
          onClick={() => { void load(); }}
          className="px-5 py-2 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
        >
          <i className="ri-refresh-line mr-1.5"></i>Réessayer
        </button>
      </div>
    );
  }

  const data = summary as EarningsSummary;

  const cards = [
    {
      icon: 'ri-shopping-bag-3-line',
      label: 'Ventes encaissées',
      value: data.gross,
      hint: `${data.count} vente${data.count > 1 ? 's' : ''} payée${data.count > 1 ? 's' : ''} en ligne`,
      tone: 'bg-primary-50 text-primary-700',
    },
    {
      icon: 'ri-percent-line',
      label: 'Frais déduits',
      value: data.fees,
      hint: 'Commission plateforme + passerelle',
      tone: 'bg-secondary-100 text-secondary-900',
    },
    {
      icon: 'ri-wallet-3-line',
      label: 'Net à retirer',
      value: data.net,
      hint: 'Déjà crédité sur votre portefeuille',
      tone: 'bg-accent-100 text-accent-900',
    },
  ];

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {cards.map((card) => (
          <div key={card.label} className="bg-background-50 border border-background-200/70 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <span className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${card.tone}`}>
                <i className={`${card.icon} text-sm`}></i>
              </span>
              <span className="text-[11px] font-semibold uppercase tracking-wide text-foreground-500">{card.label}</span>
            </div>
            <p className="text-2xl font-bold font-heading text-foreground-950">
              {card.value.toLocaleString('fr-FR', { maximumFractionDigits: 2 })}{' '}
              <span className="text-sm font-medium text-foreground-400">{currency}</span>
            </p>
            <p className="text-[11px] text-foreground-400 mt-1.5 leading-relaxed">{card.hint}</p>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="bg-background-50 border border-background-200/70 rounded-lg p-4 flex items-center gap-3">
          <span className="w-9 h-9 rounded-full bg-primary-50 text-primary-600 flex items-center justify-center flex-shrink-0">
            <i className="ri-store-3-line"></i>
          </span>
          <div className="min-w-0 flex-1">
            <p className="text-xs text-foreground-500">Boutique en ligne</p>
            <p className="text-sm font-bold text-foreground-900">
              {data.shopNet.toLocaleString('fr-FR', { maximumFractionDigits: 2 })} {currency}
              <span className="ml-1.5 text-[11px] font-medium text-foreground-400">
                ({data.shopCount} vente{data.shopCount > 1 ? 's' : ''})
              </span>
            </p>
          </div>
        </div>
        <div className="bg-background-50 border border-background-200/70 rounded-lg p-4 flex items-center gap-3">
          <span className="w-9 h-9 rounded-full bg-accent-100 text-accent-700 flex items-center justify-center flex-shrink-0">
            <i className="ri-calendar-check-line"></i>
          </span>
          <div className="min-w-0 flex-1">
            <p className="text-xs text-foreground-500">Réservations ZCalendar</p>
            <p className="text-sm font-bold text-foreground-900">
              {data.bookingNet.toLocaleString('fr-FR', { maximumFractionDigits: 2 })} {currency}
              <span className="ml-1.5 text-[11px] font-medium text-foreground-400">
                ({data.bookingCount} réservation{data.bookingCount > 1 ? 's' : ''})
              </span>
            </p>
          </div>
        </div>
      </div>

      {onRequestWithdrawal && data.net > 0 && (
        <button
          type="button"
          onClick={onRequestWithdrawal}
          className="w-full sm:w-auto px-5 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
        >
          <i className="ri-bank-card-2-line mr-1.5"></i>Demander un retrait
        </button>
      )}

      <div className="bg-background-50 border border-background-200/70 rounded-lg overflow-hidden">
        <div className="flex flex-wrap items-center gap-2 px-5 py-4 border-b border-background-200/70">
          <i className="ri-list-check-3 text-accent-500"></i>
          <h3 className="text-sm font-semibold text-foreground-900">Détail des encaissements</h3>
          <span className="ml-auto text-[11px] text-foreground-400 whitespace-nowrap">
            {data.count} opération{data.count > 1 ? 's' : ''}
          </span>
        </div>

        {data.lines.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-14 px-4 text-center">
            <div className="w-14 h-14 rounded-full bg-background-100 flex items-center justify-center mb-3">
              <i className="ri-line-chart-line text-2xl text-foreground-400"></i>
            </div>
            <p className="text-sm text-foreground-500">Aucune vente encaissée en ligne pour le moment.</p>
            <p className="text-xs text-foreground-400 mt-1 max-w-sm leading-relaxed">
              Les ventes payées en Mobile Money et les réservations réglées apparaîtront ici, avec le détail des frais.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-foreground-400 border-b border-background-200/70">
                  <th className="py-2 px-5 font-medium">Opération</th>
                  <th className="py-2 px-3 font-medium">Client</th>
                  <th className="py-2 px-3 font-medium text-right">Encaissé</th>
                  <th className="py-2 px-3 font-medium text-right">Frais</th>
                  <th className="py-2 px-3 font-medium text-right">Net</th>
                  <th className="py-2 px-5 font-medium">Date</th>
                </tr>
              </thead>
              <tbody>
                {data.lines.map((line) => (
                  <tr key={line.id} className="border-b border-background-200/50 last:border-0">
                    <td className="py-2.5 px-5">
                      <span className="flex items-center gap-2">
                        <span className={`w-6 h-6 rounded-md flex items-center justify-center flex-shrink-0 ${
                          line.kind === 'reservation' ? 'bg-accent-100 text-accent-700' : 'bg-primary-50 text-primary-600'
                        }`}>
                          <i className={`text-[11px] ${line.kind === 'reservation' ? 'ri-calendar-check-line' : 'ri-store-3-line'}`}></i>
                        </span>
                        <span className="text-foreground-800 whitespace-nowrap">{line.label}</span>
                      </span>
                    </td>
                    <td className="py-2.5 px-3 text-foreground-600 max-w-[160px] truncate">{line.client || '—'}</td>
                    <td className="py-2.5 px-3 text-right whitespace-nowrap text-foreground-800">
                      {line.gross.toLocaleString('fr-FR', { maximumFractionDigits: 2 })} {line.currency}
                    </td>
                    <td className="py-2.5 px-3 text-right whitespace-nowrap text-foreground-500">
                      −{line.fee.toLocaleString('fr-FR', { maximumFractionDigits: 2 })}
                    </td>
                    <td className="py-2.5 px-3 text-right whitespace-nowrap font-bold text-accent-700">
                      {line.net.toLocaleString('fr-FR', { maximumFractionDigits: 2 })} {line.currency}
                    </td>
                    <td className="py-2.5 px-5 text-foreground-500 whitespace-nowrap">{formatDate(line.date)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}